import { CountryCode } from '@ankor-io/common/domain/pay/supported.countries'
import { CurrencyCode } from '@ankor-io/common/domain/pay/supported.currencies'

export type GlobalAccount = {
  id: string
  awxConnectedAccountId: string
  countryCode: CountryCode
  status: GlobalAccountStatus
  nickName: string
  accountNumber: string
  currencyCode: CurrencyCode
}

/**
 * Definition from https://www.airwallex.com/docs/banking-as-a-service__create-global-accounts__global-account-statuses
 */
export enum GlobalAccountStatus {
  // Global Account is being created and not ready for use. You should wait for the status to be updated.
  PROCESSING = 'PROCESSING',
  // Global Account has been activated for use.
  ACTIVE = 'ACTIVE',
  // Global Account has been closed. Refer to close_reason for further details
  CLOSED = 'CLOSED',
  // Global Account creation has failed. Refer to failure_reason for further details
  FAILED = 'FAILED',
}

/**
 * The body for creating a global account.
 */
export type CreateGlobalAccountBody = {
  countryCode: string
  nickName: string
  email?: string
}

// for backend use only
export type CreateGlobalAccountRequest = CreateGlobalAccountBody & {
  // the id is the awx global account id
  id: string
  accountNumber: string
  currencyCode: string
  status: GlobalAccountStatus
}

export type CreateGlobalAccountAWXRequest = {
  country_code: CountryCode
  nick_name: string
  request_id: string
  required_features: GlobalAccountFeature[]
  // only required for Canada, thank you Canada!
  alternate_account_identifier?: {
    email: string
  }
}

export type UpdateGlobalAccountAWXRequest = {
  id: string
  nick_name: string
}

// for backend use only
export type UpdateGlobalAccountRequest = UpdateGlobalAccountBody & {
  // the id of the global account to update
  id: string
}

/**
 * The body for updating a global account.
 */
export type UpdateGlobalAccountBody = {
  nickName: string
}

/**
 * This is declared as a separate type so the backend type can evolve without affecting the frontend.
 */
export type GlobalAccountListingPayload = {
  id: string
  countryCode: string
  status: GlobalAccountStatus
  nickName: string
  accountNumber: string
  currencyCode: string
}

/**
 * See more about this type here https://www.airwallex.com/docs/api#/Core_Resources/Global_Accounts/_api_v1_global_accounts__id_/get
 */
export type GlobalAccountPayload = GlobalAccountListingPayload & {
  // we do not store this locally, this is not the nickName, this is the AWX account name
  accountName?: string
  // the account type from AWX
  accountType?: string
  // only required for Canada
  alternateAccountIdentifier?: {
    email: string
  }
  // the reason the account was closed
  closeReason?: string
  // Target currency that incoming funds will be converted to.
  depositConversionCurrency: string
  // the reason the account creation failed
  failureReason?: string
  // the IBAN of the account
  iban: string
  institution: {
    address: string
    branchName: string
    city: string
    name: string
    zipCode: string
  }
  // unique request identifier required by AWX
  requestId: string
  // Bank SWIFT code
  swiftCode: string
  supportedFeatures: {
    currency: string
    localClearingSystem: string
    routingCodes: { type: string; value: string }[]
    transferMethod: string
    type: string
  }[]
}

export type GlobalAccountFeature = {
  currency: CurrencyCode
  transfer_method: GlobalAccountTransferMethod
}

export enum GlobalAccountTransferMethod {
  LOCAL = 'LOCAL',
  SWIFT = 'SWIFT',
}

/**
 * Global account support in Ankor Pay. Each country is defaulted to receive funds in local currency.
 * Some countries support SWIFT transfers for other currencies.
 * This must match the same list in supported.countries.ts
 * TODO: review this with the AWX team to make sure this is correct
 * https://www.airwallex.com/docs/global-treasury__market-and-infrastructure-coverage#Global%20Accounts
 *
 * NOTE: This currently follows the 5 priority countries for V1:
 * https://www.notion.so/ankor-software/Currencies-Countries-Supported-27ead1bd6f7c809aa6c0f9aff647ede9?source=copy_link#27fad1bd6f7c80f2a65be97626bb03af
 */
export const GLOBAL_ACCOUNT_SUPPORTED_COUNTRIES: Partial<{ [key in CountryCode]: GlobalAccountFeature[] }> = {
  // [CountryCode.AE]: [
  //   {
  //     currency: CurrencyCode.AED,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  [CountryCode.AU]: [
    {
      currency: CurrencyCode.AUD,
      transfer_method: GlobalAccountTransferMethod.LOCAL,
    },
  ],
  // [CountryCode.CA]: [
  //   {
  //     currency: CurrencyCode.CAD,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  // [CountryCode.DE]: [
  //   {
  //     currency: CurrencyCode.EUR,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  [CountryCode.DK]: [
    {
      currency: CurrencyCode.DKK,
      transfer_method: GlobalAccountTransferMethod.LOCAL,
    },
    {
      currency: CurrencyCode.DKK,
      transfer_method: GlobalAccountTransferMethod.SWIFT,
    },
  ],
  // [CountryCode.EE]: [
  //   {
  //     currency: CurrencyCode.EUR,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  [CountryCode.GB]: [
    {
      currency: CurrencyCode.GBP,
      transfer_method: GlobalAccountTransferMethod.LOCAL,
    },
    {
      currency: CurrencyCode.GBP,
      transfer_method: GlobalAccountTransferMethod.SWIFT,
    },
  ],
  // [CountryCode.HK]: [
  //   {
  //     currency: CurrencyCode.HKD,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  //   {
  //     currency: CurrencyCode.HKD,
  //     transfer_method: GlobalAccountTransferMethod.SWIFT,
  //   },
  // ],
  // [CountryCode.ID]: [
  //   {
  //     currency: CurrencyCode.IDR,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  // [CountryCode.IL]: [
  //   {
  //     currency: CurrencyCode.ILS,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  // [CountryCode.MX]: [
  //   {
  //     currency: CurrencyCode.MXN,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  [CountryCode.NL]: [
    {
      currency: CurrencyCode.EUR,
      transfer_method: GlobalAccountTransferMethod.LOCAL,
    },
  ],
  // [CountryCode.NZ]: [
  //   {
  //     currency: CurrencyCode.NZD,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  // [CountryCode.PH]: [
  //   {
  //     currency: CurrencyCode.PHP,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  // [CountryCode.PL]: [
  //   {
  //     currency: CurrencyCode.PLN,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  // ],
  // [CountryCode.SG]: [
  //   {
  //     currency: CurrencyCode.SGD,
  //     transfer_method: GlobalAccountTransferMethod.LOCAL,
  //   },
  //   {
  //     currency: CurrencyCode.SGD,
  //     transfer_method: GlobalAccountTransferMethod.SWIFT,
  //   },
  // ],
  [CountryCode.US]: [
    {
      currency: CurrencyCode.USD,
      transfer_method: GlobalAccountTransferMethod.LOCAL,
    },
    {
      currency: CurrencyCode.USD,
      transfer_method: GlobalAccountTransferMethod.SWIFT,
    },
  ],
}
